import { createFileRoute, Link } from '@tanstack/react-router';
import { CONSOLIDATED, useActiveSelection } from '../../stores/useCompanyStore';

export const Route = createFileRoute('/_auth/reports/')({
  component: ReportsHome,
});

const REPORTS = [
  {
    to: '/reports/balance-sheet',
    title: 'Balance Sheet',
    desc: 'Assets, liabilities, and equity as of a date.',
  },
  {
    to: '/reports/income-statement',
    title: 'Income Statement',
    desc: 'Revenue and expenses over a period (P&L).',
  },
  {
    to: '/reports/weekly-profitability',
    title: 'Weekly Profitability',
    desc: 'Net income per calendar week — by year, quarter, or month.',
  },
  {
    to: '/reports/trial-balance',
    title: 'Trial Balance',
    desc: "Every account's debit/credit balance as of a date.",
  },
  {
    to: '/reports/general-ledger',
    title: 'General Ledger',
    desc: 'Transaction detail with running balances.',
  },
  {
    to: '/reports/cash-flow',
    title: 'Cash Flow',
    desc: 'Cash movement by activity (indirect method).',
  },
  {
    to: '/reports/cash-flow-forecast',
    title: 'Cash-Flow Forecast',
    desc: '13-week projection from A/R, A/P, loans, and recurring entries.',
  },
  {
    to: '/reports/ar-aging',
    title: 'A/R Aging',
    desc: 'Outstanding customer balances bucketed by age.',
  },
  {
    to: '/reports/ap-aging',
    title: 'A/P Aging',
    desc: 'Outstanding vendor balances bucketed by age.',
  },
  {
    to: '/reports/budget-vs-actual',
    title: 'Budget vs. Actual',
    desc: 'Budgeted vs. actual income & expense, with variance.',
  },
  {
    to: '/reports/segment-income-statement',
    title: 'P&L by Segment',
    desc: 'Income statement broken out by Class or Location.',
  },
  {
    to: '/report-builder',
    title: 'Report Builder',
    desc: 'Build a custom report — filter, group, and total your transactions.',
  },
  {
    to: '/reports/1099-nec',
    title: '1099-NEC',
    desc: 'Reportable nonemployee compensation by vendor for the year.',
  },
] as const;

function ReportsHome() {
  const selection = useActiveSelection();
  const consolidated = selection === CONSOLIDATED;

  if (!selection) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Reports</h1>
        <p className="muted">
          Pick a company — or “Consolidated” — in the switcher to view its reports.
        </p>
        <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
          Go to Companies
        </Link>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ fontSize: 24, margin: '0 0 16px' }}>
        Reports
        {consolidated && <span className="tag" style={{ marginLeft: 8 }}>Consolidated</span>}
      </h1>
      {consolidated && (
        <p className="muted" style={{ marginTop: -8, marginBottom: 16, fontSize: 13 }}>
          Combining all companies marked “Include in Consolidated.” The General Ledger is
          per-company only.
        </p>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 16,
        }}
      >
        {REPORTS.map((r) => (
          <Link
            key={r.to}
            to={r.to}
            className="card"
            style={{ textDecoration: 'none', display: 'block', padding: 20 }}
          >
            <div style={{ fontWeight: 600, fontSize: 16, color: 'var(--text-strong)' }}>
              {r.title}
            </div>
            <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>
              {r.desc}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
